/**
 * Plan catalog helpers — tier resolution, upgrade-path logic, sort_order-based icon indexing.
 *
 * v1.1 - Session 501: add computeSetupFeeDisplay for ToS price de-hardcode - 2026-06-13
 * v1.0 - STORY-10E-C: extracted from Billing/Landing for testability; catalog-driven with no tier-name literals - 2026-06-10
 *
 * Design contract:
 *  - subscriptionTiers(): returns only payment_mode==='subscription' tiers, ascending sort_order
 *  - findUpgradeTier(): highest-sort_order subscription tier (the "enterprise" concept is gone)
 *  - resolvePlanToTier(): matches planId against tier_id OR aliases[] (alias-aware)
 *  - hasUpgradePath(): true iff a higher-sort_order subscription tier exists for the current plan
 *  - tierSortPosition(): position (0-based) of a tier in the sorted subscription list
 *  - computeSetupFeeDisplay(): catalog-driven setup fee label; null if multiple distinct values
 */

import { Tier } from '../services/api';

export function subscriptionTiers(tiers: Tier[]): Tier[] {
  return tiers
    .filter((t) => t.payment_mode === 'subscription')
    .sort((a, b) => a.sort_order - b.sort_order);
}

export function findUpgradeTier(tiers: Tier[]): Tier | null {
  const sub = subscriptionTiers(tiers);
  return sub.length > 0 ? sub[sub.length - 1] : null;
}

export function resolvePlanToTier(planId: string | undefined, tiers: Tier[]): Tier | null {
  if (!planId) return null;
  return (
    tiers.find(
      (t) => t.tier_id === planId || (Array.isArray(t.aliases) && t.aliases.includes(planId))
    ) ?? null
  );
}

export function isFreeTier(tier: Tier | null | undefined): boolean {
  return !tier || tier.price_monthly === 0;
}

export function hasUpgradePath(currentPlanId: string | undefined, tiers: Tier[]): boolean {
  const sub = subscriptionTiers(tiers);
  if (sub.length === 0) return false;
  const highest = sub[sub.length - 1];
  if (!currentPlanId) return true;
  const current = resolvePlanToTier(currentPlanId, sub);
  if (!current) return true; // unrecognised plan — always show upgrade
  return current.sort_order < highest.sort_order;
}

export function tierSortPosition(tier: Tier | null | undefined, tiers: Tier[]): number {
  if (!tier) return 0;
  const sub = subscriptionTiers(tiers);
  const idx = sub.findIndex((t) => t.tier_id === tier.tier_id);
  return idx >= 0 ? idx : 0;
}

// Returns "$NNN" if all paid subscription tiers share a single setup_fee value; null otherwise.
// Callers should fall back to generic wording when this returns null.
export function computeSetupFeeDisplay(tiers: Tier[]): string | null {
  const paidSubs = tiers.filter(
    (t) => t.payment_mode === 'subscription' && t.setup_fee > 0
  );
  if (paidSubs.length === 0) return null;
  const fees = paidSubs.map((t) => t.setup_fee);
  const unique = fees.filter((v, i, arr) => arr.indexOf(v) === i);
  return unique.length === 1 ? `$${unique[0]}` : null;
}
